import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ActionKey, canPerformAction } from '@/auth/actionMatrix';
import { lossesService } from '@/services/losses/losses.service';
import { RootState } from '@/store';
import type { Loss, LossPayload, ResponsibleUser } from '@/types/loss';


type LossForm = {
  lossType: string;
  description: string;
  responsible: ResponsibleUser | null;
  lossAmount: string;
  occurredAt: string;
  isMoneyLost: boolean;
};

const localDateTimeValue = (value = new Date().toISOString()) => {
  const date = new Date(value);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
};

const emptyForm = (): LossForm => ({
  lossType: '',
  description: '',
  responsible: null,
  lossAmount: '',
  occurredAt: localDateTimeValue(),
  isMoneyLost: false,
});

const formatLossAmount = (value: Loss['loss_amount']) => {
  if (value === null) return '—';
  const [integerPart, decimalPart = ''] = String(value).replace(',', '.').split('.');
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const trimmedDecimal = decimalPart.replace(/0+$/, '');
  return trimmedDecimal ? `${groupedInteger},${trimmedDecimal}` : groupedInteger;
};

const errorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  }
  return 'Не удалось выполнить операцию. Попробуйте ещё раз.';
};

export default function Losses() {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canManage = canPerformAction(currentUser, ActionKey.LOSSES_UPDATE);
  const [items, setItems] = useState<Loss[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState<'all' | 'lost' | 'not_lost'>('all');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [revision, setRevision] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Loss | null>(null);
  const [form, setForm] = useState<LossForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [responsibleUsers, setResponsibleUsers] = useState<ResponsibleUser[]>([]);
  const [responsibleSearch, setResponsibleSearch] = useState('');
  const [responsibleLoading, setResponsibleLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(true);
      setLoadError('');
      void lossesService.list({
        q: search.trim() || undefined,
        is_money_lost: outcome === 'all' ? undefined : outcome === 'lost',
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }).then((response) => {
        setItems(response.items);
        setTotal(response.total);
      }).catch((error) => {
        setLoadError(errorMessage(error));
      }).finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [outcome, page, revision, rowsPerPage, search]);

  useEffect(() => {
    if (!dialogOpen) return;
    const timer = window.setTimeout(() => {
      setResponsibleLoading(true);
      void lossesService.responsibleUsers(responsibleSearch)
        .then((users) => setResponsibleUsers(users))
        .catch(() => setResponsibleUsers([]))
        .finally(() => setResponsibleLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dialogOpen, responsibleSearch]);

  const responsibleOptions = useMemo(() => {
    if (!form.responsible) return responsibleUsers;
    if (responsibleUsers.some((user) => user.id === form.responsible?.id)) {
      return responsibleUsers;
    }
    return [form.responsible, ...responsibleUsers];
  }, [form.responsible, responsibleUsers]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setResponsibleSearch('');
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((item: Loss) => {
    setEditing(item);
    setForm({
      lossType: item.loss_type,
      description: item.description,
      responsible: item.responsible_user_id
        ? { id: item.responsible_user_id, username: item.responsible_name }
        : null,
      lossAmount: item.loss_amount === null ? '' : String(item.loss_amount),
      occurredAt: localDateTimeValue(item.occurred_at),
      isMoneyLost: item.is_money_lost,
    });
    setFormError('');
    setResponsibleSearch('');
    setDialogOpen(true);
  }, []);

  const submit = async () => {
    if (!form.lossType.trim() || !form.description.trim() || !form.lossAmount.trim() || !form.occurredAt) {
      setFormError('Заполните тип, сумму, описание потери и дату события.');
      return;
    }
    const normalizedAmount = form.lossAmount.trim().replace(',', '.');
    if (!/^\d{1,16}(\.\d{1,2})?$/.test(normalizedAmount)) {
      setFormError('Укажите неотрицательную сумму, не более двух знаков после запятой.');
      return;
    }
    const payload: LossPayload = {
      loss_type: form.lossType.trim(),
      description: form.description.trim(),
      responsible_user_id: form.responsible?.id ?? null,
      loss_amount: normalizedAmount,
      occurred_at: new Date(form.occurredAt).toISOString(),
      is_money_lost: form.isMoneyLost,
    };
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await lossesService.update(editing.id, payload);
      } else {
        await lossesService.create(payload);
      }
      setDialogOpen(false);
      setRevision((value) => value + 1);
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4">Потери</Typography>
          <Typography color="text.secondary">
            Реестр инцидентов и их фактического финансового результата.
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Добавить потерю
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Поиск"
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(0); }}
            placeholder="Тип, ответственный или описание потери"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          <TextField
            select
            label="Финансовый итог"
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value as typeof outcome);
              setPage(0);
            }}
            sx={{ minWidth: 230 }}
          >
            <MenuItem value="all">Все</MenuItem>
            <MenuItem value="lost">Деньги потеряны</MenuItem>
            <MenuItem value="not_lost">Без фактической потери</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Когда было</TableCell>
                <TableCell>Тип потери</TableCell>
                <TableCell>Описание потери</TableCell>
                <TableCell>Ответственный</TableCell>
                <TableCell align="right">Сумма потери</TableCell>
                <TableCell>Финансовый итог</TableCell>
                {canManage && <TableCell align="right">Действия</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(item.occurred_at).toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell>{item.loss_type}</TableCell>
                  <TableCell sx={{ minWidth: 320, maxWidth: 640, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {item.description}
                  </TableCell>
                  <TableCell>{item.responsible_name}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatLossAmount(item.loss_amount)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={item.is_money_lost ? 'error' : 'success'}
                      label={item.is_money_lost ? 'Деньги потеряны' : 'Без потери'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Редактировать">
                        <IconButton onClick={() => openEdit(item)}><EditIcon /></IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!items.length && !loading && (
                <TableRow><TableCell colSpan={canManage ? 7 : 6}>Записей пока нет</TableCell></TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={canManage ? 7 : 6} align="center">
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Строк на странице"
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Редактировать потерю' : 'Добавить потерю'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              required
              label="Тип потери"
              value={form.lossType}
              onChange={(event) => setForm((value) => ({ ...value, lossType: event.target.value }))}
              inputProps={{ maxLength: 100 }}
            />
            <Autocomplete
              options={responsibleOptions}
              value={form.responsible}
              loading={responsibleLoading}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option.username}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setForm((current) => ({ ...current, responsible: value }))}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setResponsibleSearch(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ответственный"
                  helperText="Можно оставить без ответственного"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>{responsibleLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>
                    ),
                  }}
                />
              )}
            />
            <TextField
              required
              type="datetime-local"
              label="Когда произошло"
              value={form.occurredAt}
              onChange={(event) => setForm((value) => ({ ...value, occurredAt: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              required
              label="Сумма потери"
              value={form.lossAmount}
              onChange={(event) => setForm((value) => ({ ...value, lossAmount: event.target.value }))}
              placeholder="0,00"
              inputProps={{ inputMode: 'decimal', maxLength: 19 }}
              helperText="Укажите сумму без знака валюты, до двух знаков после запятой."
            />
            <TextField
              required
              label="Описание потери"
              value={form.description}
              onChange={(event) => setForm((value) => ({ ...value, description: event.target.value }))}
              multiline
              minRows={6}
              placeholder="Укажите ID транзакции, трейдера, обстоятельства и другие важные детали"
              helperText="Опишите ситуацию в свободной форме."
              inputProps={{ maxLength: 10000 }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isMoneyLost}
                  onChange={(event) => setForm((value) => ({ ...value, isMoneyLost: event.target.checked }))}
                  color="error"
                />
              )}
              label={form.isMoneyLost ? 'Деньги фактически потеряны' : 'Деньги не потеряны / возмещены'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
